<script setup lang="ts">
/* ============================================================
   DocView — one reviewed document: prose + margin cards + connectors

   This is the heart of the reader. The prose subtree is rendered
   via v-html and then managed imperatively (highlight <mark>s,
   margin cards, SVG connectors) exactly as prototype/reader.html
   does — Vue reactivity does not own the prose tree, because the
   comment-anchoring algorithm splits text nodes and measures live
   DOM geometry. Cards are built imperatively too so layoutMargin's
   offsetHeight measurements are accurate.

   Lifecycle ported from the prototype: createDraft → commit/cancel,
   view → edit → save/cancel, delete, active/hover linking, relayout
   on resize, scroll persistence.
   ============================================================ */

import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import type { Tab, Comment } from '@/types'
import { AGENT_AUTHOR_ID, AGENT_AUTHOR_NAME } from '@/types'
import { renderMarkdownWithLineInfo, highlightCodeBlocks } from '@/composables/useMarkdown'
import { usePersistence } from '@/composables/usePersistence'
import { pushToast } from '@/composables/useToasts'
import { relativeTime } from '@/composables/utils'
import { useReviewPrefs } from '@/composables/useReviewPrefs'
import { useIdentity, authorColor } from '@/composables/useIdentity'
import { recordMetric } from '@/composables/useAnalytics'
import { preserveCommentSelection } from '@/composables/selectionCopy'
import { layoutMargin, type LayoutItem } from '@/composables/layoutMargin'
import {
  findEnclosingBlock,
  findPrecedingHeading,
  approxLineWithinBlock,
  charOffsetWithin,
  makeRangeFromOffsets,
  clampRangeToProse,
  wrapRangeWithMark,
  unwrapMarks,
  findTextRange,
  findHeadingAnchor,
} from '@/composables/sourceMap'

const props = defineProps<{
  tab: Tab
  active: boolean
  /** Ephemeral docs (Welcome.md / Docs.md) render normally but never persist
   *  to IndexedDB or the recent list. */
  ephemeral?: boolean
  /** Reading-only (Docs.md): no comment gutter — prose renders centered, and
   *  selecting text does not start a comment. */
  plain?: boolean
}>()

const { saveTabSoon, saveTabImmediate } = usePersistence()
const { author: myAuthor, color: myColor } = useIdentity()

const rootEl = ref<HTMLElement | null>(null)
const proseEl = ref<HTMLElement | null>(null)
const marginEl = ref<HTMLElement | null>(null)
const connectorsEl = ref<SVGSVGElement | null>(null)
const scrollEl = ref<HTMLElement | null>(null)
const metaEl = ref<HTMLElement | null>(null)

// transient lifecycle ids (per-doc)
let draftCommentId: string | null = null
let editingCommentId: string | null = null
let ro: ResizeObserver | null = null
let scrollTick: ReturnType<typeof setTimeout> | null = null

const tab = props.tab

// ---------- mobile comment sheet ----------
// On phones the comment gutter collapses into a bottom sheet (see the
// @media block in app.css). These drive its peek/expand state + count; on
// desktop the handle is hidden and none of this has any visual effect.
// The sheet has three states: collapsed (just the peek handle), expanded (the
// full scrollable list), and focused (shrunk to a single tapped comment, with
// the document scrolled so its highlight sits at the top). focusedCommentId
// names the one card shown in focused mode.
const sheetMode = ref<'collapsed' | 'expanded' | 'focused'>('collapsed')
const focusedCommentId = ref<string | null>(null)
// The peek label counts committed comments; the sheet's *visibility*
// (has-comments) keys off any card at all — including a live draft — so the
// very first comment on an empty plan has somewhere to render. Cards are
// rendered imperatively and comments are mutated in place (e.g. `delete
// comment._draft` on commit, or restored from IndexedDB outside Vue's view), so
// a computed over tab.comments doesn't reliably track them. Instead these are
// plain refs pushed from syncSheetCounts(), which runs from the same spots that
// re-render the cards.
const commentCount = ref(0)
const hasCards = ref(false)
// Counts that drive the reader's quick filter control (shown only when the doc
// actually carries those layers).
const agentCount = ref(0)
const resolvedCount = ref(0)
/** A thread is settled when someone resolved it. */
function isResolved(c: Comment): boolean {
  return !!c.resolved
}
/** True for a thread the agent opened (its author is the sentinel). */
function isAgentThread(c: Comment): boolean {
  return c.author?.id === AGENT_AUTHOR_ID
}
/** A comment's accent color — the hue of its highlight, reused for the connector
 *  line and the card's active outline so they all match (instead of always
 *  yellow). Mirrors the mark CSS: author hue, agent accent, or the default
 *  yellow bar. Returns a CSS color or a `var(--token)`. */
function commentAccent(c: Comment): string {
  const authorId = c.author?.id ?? (c._draft ? myAuthor.value.id : null)
  if (authorId === AGENT_AUTHOR_ID) return 'var(--accent)'
  if (authorId) return authorColor(authorId)
  return 'var(--highlight-bd)'
}
function syncSheetCounts(): void {
  const list = tab.comments || []
  commentCount.value = list.filter((c) => !c._draft).length
  hasCards.value = list.length > 0
  agentCount.value = list.filter((c) => isAgentThread(c)).length
  resolvedCount.value = list.filter((c) => !c._draft && isResolved(c)).length
}

// ---------- review filters (agent / resolved) ----------
// Persisted, shared with the Settings menu (useReviewPrefs). Hiding a layer
// pulls its cards from the column and drops its highlights back to plain text;
// relayout skips the hidden cards so the column repacks.
const { showAgent, showResolved, toggleAgent, toggleResolved } = useReviewPrefs()
function commentHiddenByPref(c: Comment): boolean {
  if (isAgentThread(c) && !showAgent.value) return true
  if (isResolved(c) && !showResolved.value) return true
  return false
}
function applyReviewPrefs(): void {
  const marginArea = marginEl.value
  const prose = proseEl.value
  if (!marginArea || !prose) return
  for (const c of tab.comments) {
    const hidden = commentHiddenByPref(c)
    marginArea
      .querySelectorAll(`.margin-card[data-comment-id="${c.id}"]`)
      .forEach((el) => el.classList.toggle('is-pref-hidden', hidden))
    prose
      .querySelectorAll(`mark[data-comment-id="${c.id}"]`)
      .forEach((el) => el.classList.toggle('is-pref-hidden', hidden))
  }
  relayout()
}
watch([showAgent, showResolved], () => applyReviewPrefs())
// My resolved color changes when I pick a new swatch OR the theme flips between
// light and dark; either way, repaint marks, rails, bylines, and connectors.
watch(myColor, () => rerenderFromState())
const isMobile = (): boolean => window.matchMedia('(max-width: 760px)').matches

// --- sheet geometry -------------------------------------------------------
function sheetPeek(): number {
  const handle = marginEl.value?.querySelector<HTMLElement>('.sheet-handle')
  return handle?.offsetHeight ?? 56
}
function collapsedOffset(): number {
  const el = marginEl.value
  return el ? Math.max(0, el.offsetHeight - sheetPeek()) : 0
}
/** translateY (px) for the focused detent: peek + the active card, the rest
 *  pushed off the bottom. Non-active cards are display:none in focused mode, so
 *  the active card is the only thing under the handle. */
function focusedOffset(): number {
  const el = marginEl.value
  if (!el) return 0
  const card = el.querySelector<HTMLElement>('.margin-card.is-active')
  const visible = sheetPeek() + (card?.offsetHeight ?? 0) + 16
  return Math.max(0, Math.min(collapsedOffset(), el.offsetHeight - visible))
}
function currentOffset(): number {
  if (sheetMode.value === 'expanded') return 0
  if (sheetMode.value === 'focused') return focusedOffset()
  return collapsedOffset()
}
/** Push the sheet to where its mode says it should sit. collapsed/expanded ride
 *  the CSS class translateY; focused needs a measured pixel offset the class
 *  can't express, so it gets an inline transform. */
function applySheetTransform(): void {
  const el = marginEl.value
  if (!el) return
  el.style.transform = sheetMode.value === 'focused' ? `translateY(${focusedOffset()}px)` : ''
}
watch(sheetMode, () => nextTick(applySheetTransform))
/** Re-fit the focused sheet after its single card changes height (entering or
 *  leaving edit, or the note growing as you type). No-op unless focused. */
function refreshFocusedSheet(): void {
  if (isMobile() && sheetMode.value === 'focused') nextTick(applySheetTransform)
}

// --- sheet controls -------------------------------------------------------
function toggleSheet(): void {
  // From focused or collapsed the handle reveals the full list; expanded folds.
  sheetMode.value = sheetMode.value === 'expanded' ? 'collapsed' : 'expanded'
  focusedCommentId.value = null
}
function collapseSheet(): void {
  sheetMode.value = 'collapsed'
  focusedCommentId.value = null
}
/** Tapping a comment (its card or its highlight) focuses just that one: the
 *  sheet shrinks to show only its card and the document scrolls so the
 *  highlight sits at the top of the screen. Desktop keeps its centred reveal. */
function focusComment(id: string): void {
  // Clicking a resolved (collapsed) thread's highlight expands its card, so
  // the highlight is a way into the settled conversation, not a dead end.
  const c = tab.comments.find((x) => x.id === id)
  if (c && isResolved(c) && c._collapsed !== false) {
    c._collapsed = false
    replaceCard(c)
  }
  setActiveComment(id, false)
  if (!isMobile()) {
    scrollHighlightIntoView(id)
    return
  }
  focusedCommentId.value = id
  sheetMode.value = 'focused'
  nextTick(() => {
    applySheetTransform()
    scrollHighlightToTop(id)
  })
}
/** Committed comments in document order — the order the pager steps through. */
function orderedComments(): Comment[] {
  return (tab.comments || [])
    .filter((c) => !c._draft)
    .slice()
    .sort((a, b) => (a.domRangeStart ?? 0) - (b.domRangeStart ?? 0))
}
/** Pan to the previous (-1) / next (+1) comment in reading order and focus it.
 *  Clamps at the ends (the Prev/Next buttons disable there). With nothing
 *  focused (index -1), Next steps into the first comment. */
function focusAdjacent(dir: number): void {
  const ordered = orderedComments()
  if (ordered.length === 0) return
  const nextIdx = pagerIndex.value + dir
  if (nextIdx < 0 || nextIdx >= ordered.length) return
  focusComment(ordered[nextIdx].id)
}

// Reactive pager position + end states for the Prev/Next buttons. orderedIds
// re-tracks via commentCount (a ref bumped on every add/remove), and the index
// follows the focused comment, so the buttons disable correctly at the ends.
const orderedIds = computed(() => {
  void commentCount.value
  return orderedComments().map((c) => c.id)
})
const pagerIndex = computed(() =>
  focusedCommentId.value ? orderedIds.value.indexOf(focusedCommentId.value) : -1,
)
const prevDisabled = computed(() => pagerIndex.value <= 0)
const nextDisabled = computed(() => pagerIndex.value >= orderedIds.value.length - 1)

// --- handle drag: a real draggable sheet, not a button in slider's clothing ---
// The sheet is positioned by translateY: dragging the handle moves it live with
// the transition off; releasing snaps to collapsed or expanded (whichever is
// nearer), leaving focused mode. A press that barely moves is treated as a tap
// and falls through to onHandleClick.
let dragStartY = 0
let dragBaseOffset = 0
let dragCollapsedOffset = 0
let dragMoved = false
let suppressHandleClick = false

function onHandlePointerDown(e: PointerEvent): void {
  if (!isMobile()) return
  const el = marginEl.value
  const handle = e.currentTarget as HTMLElement
  if (!el) return
  dragCollapsedOffset = collapsedOffset()
  dragBaseOffset = currentOffset()
  dragStartY = e.clientY
  dragMoved = false
  // Start each gesture fresh: a prior drag may have set this without a trailing
  // click to consume it (drags often emit no click), which would otherwise
  // swallow this tap.
  suppressHandleClick = false
  el.classList.add('is-dragging')
  handle.setPointerCapture?.(e.pointerId)
}
function onHandlePointerMove(e: PointerEvent): void {
  const el = marginEl.value
  if (!el || !el.classList.contains('is-dragging')) return
  const delta = e.clientY - dragStartY
  if (Math.abs(delta) > 4) dragMoved = true
  const offset = Math.min(dragCollapsedOffset, Math.max(0, dragBaseOffset + delta))
  el.style.transform = `translateY(${offset}px)`
}
function onHandlePointerUp(e: PointerEvent): void {
  const el = marginEl.value
  if (!el || !el.classList.contains('is-dragging')) return
  el.classList.remove('is-dragging')
  ;(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId)
  if (dragMoved) {
    suppressHandleClick = true // a drag just happened — don't also toggle on click
    const delta = e.clientY - dragStartY
    const offset = Math.min(dragCollapsedOffset, Math.max(0, dragBaseOffset + delta))
    sheetMode.value = offset < dragCollapsedOffset / 2 ? 'expanded' : 'collapsed'
    focusedCommentId.value = null
    // Hand the inline transform back to the CSS class once Vue has applied the
    // detent, so it eases from where the finger left off rather than snapping.
    nextTick(applySheetTransform)
  } else {
    // A tap (handled by onHandleClick) — just restore the mode's resting transform.
    applySheetTransform()
  }
}
function onHandleClick(): void {
  if (suppressHandleClick) {
    suppressHandleClick = false
    return
  }
  toggleSheet()
}

// ---------- mobile selection trigger ----------
// Desktop starts a draft on mouseup; touch has no equivalent moment (mouseup
// fires before the selection settles, and the OS callout owns the gesture).
// Instead we watch selectionchange (debounced) and, once a real selection
// lands in the prose, float a "Comment" pill. Tapping a button outside the
// selection tends to collapse it, so we stash the range and restore it before
// starting the draft.
const pillVisible = ref(false)
let pillTimer: ReturnType<typeof setTimeout> | null = null
let pendingRange: Range | null = null

function currentProseRange(): Range | null {
  if (props.plain || !props.active) return null
  if (draftCommentId || editingCommentId) return null
  const prose = proseEl.value
  if (!prose) return null
  const sel = window.getSelection()
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null
  const range = sel.getRangeAt(0)
  if (!prose.contains(range.commonAncestorContainer)) return null
  if (!range.toString().trim()) return null
  return range
}

function onSelectionChange(): void {
  if (!isMobile()) return
  if (pillTimer) clearTimeout(pillTimer)
  pillTimer = setTimeout(() => {
    const range = currentProseRange()
    pendingRange = range ? range.cloneRange() : null
    pillVisible.value = range !== null
  }, 250)
}

function onCommentPill(): void {
  // Tapping the pill (or the OS callout) collapses the live selection on touch,
  // and re-adding a Range to window.getSelection() is unreliable on mobile
  // Safari. So drive the draft off the Range we snapshotted when the pill
  // appeared, not off the (now-collapsed) selection.
  if (pillTimer) clearTimeout(pillTimer)
  const range = pendingRange
  pillVisible.value = false
  pendingRange = null
  if (range) startDraftFromRange(range)
}

// ---------- escape helpers ----------
function escapeHtml(s: string): string {
  return String(s).replace(
    /[&<>"']/g,
    (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  )
}
const escapeAttr = escapeHtml

/** Scroll behavior that honors the user's reduced-motion preference (CSS
 *  scroll-behavior doesn't apply to programmatic scrollIntoView/scrollTo). */
function scrollBehavior(): ScrollBehavior {
  return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
}

// ---------- doc meta header (provenance date + review-state chips) ----------
// The document name lives in the tab + the H1 below, so the header leads with
// the provenance date instead.
function fullDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}
/** Roomy, human "2 days ago" companion to the absolute date. */
function verboseAge(ms: number): string {
  const s = Math.floor((Date.now() - ms) / 1000)
  if (s < 5) return 'just now'
  if (s < 60) return `${s} second${s === 1 ? '' : 's'} ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m} min${m === 1 ? '' : 's'} ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`
  const d = Math.floor(h / 24)
  if (d < 30) return `${d} day${d === 1 ? '' : 's'} ago`
  const mo = Math.floor(d / 30)
  return `${mo} month${mo === 1 ? '' : 's'} ago`
}
function refreshDocMeta(): void {
  // Keep the sheet's peek count/visibility in lockstep with the byline; this
  // runs after every commit/edit/delete/restore, so the two never disagree.
  syncSheetCounts()
  const el = metaEl.value
  if (!el) return
  const committed = (tab.comments || []).filter((c) => !c._draft).length
  const hasNotes = (tab.generalNotes || '').trim().length > 0
  const hasSummary = (tab.summary || '').trim().length > 0
  const editedDifferent = Math.abs(tab.updatedAt - tab.createdAt) > 60000
  const words = (tab.markdown || '').trim().split(/\s+/).filter(Boolean).length
  const minutes = Math.max(1, Math.round(words / 220)) // ~220 wpm

  // A plan opened from a server share leads with its real upload date; a local
  // file/paste leads with when this reader first opened it.
  const verb = tab.publishedAt ? 'Shared' : 'Opened'
  const stamp = tab.publishedAt ?? tab.createdAt

  // One quiet, dot-separated byline that recedes behind the H1: provenance
  // date with its recency in parens, then review facts.
  const facts: string[] = [
    `<span>${verb} <time datetime="${new Date(stamp).toISOString()}">${escapeHtml(fullDate(stamp))}</time> <span class="doc-meta-age">(${escapeHtml(verboseAge(stamp))})</span></span>`,
  ]
  if (editedDifferent) {
    facts.push(
      `<span>edited <time datetime="${new Date(tab.updatedAt).toISOString()}">${escapeHtml(relativeTime(tab.updatedAt))}</time></span>`,
    )
  }
  facts.push(
    `<span${committed > 0 ? ' class="doc-meta-active"' : ''}>${committed} comment${committed === 1 ? '' : 's'}</span>`,
  )
  if (hasSummary) facts.push(`<span>summary</span>`)
  if (hasNotes) facts.push(`<span>notes</span>`)
  facts.push(`<span title="${words.toLocaleString()} words">~${minutes} min read</span>`)

  el.innerHTML = `<div class="doc-meta-byline">${facts.join(
    '<span class="doc-meta-sep" aria-hidden="true"></span>',
  )}</div>`
}

function saveSoon(): void {
  if (!props.ephemeral) saveTabSoon(tab)
  refreshDocMeta()
}
function saveNow(): void {
  refreshDocMeta()
  if (!props.ephemeral) saveTabImmediate(tab)
}

// ---------- relayout ----------
function relayout(): void {
  const marginArea = marginEl.value
  const svg = connectorsEl.value
  const prose = proseEl.value
  if (!marginArea || !svg || !prose) return
  const items: LayoutItem[] = tab.comments
    .map((c): LayoutItem | null => {
      const cardEl = marginArea.querySelector<HTMLElement>(
        `.margin-card[data-comment-id="${c.id}"]`,
      )
      const markEl = prose.querySelector<HTMLElement>(`mark[data-comment-id="${c.id}"]`)
      let anchorEl = markEl
      // A card whose passage couldn't be re-anchored (the text changed or was
      // removed) has no mark — park it beside its section heading so it still
      // has a vertical home instead of stacking at the top. It references no
      // live words, so it gets a home but no connector line (noConnector).
      if (!anchorEl) {
        anchorEl = findHeadingAnchor(prose, c.blockHint)
      }
      if (!cardEl || !anchorEl) return null
      // A filtered-out layer is display:none — skip it so the column packs around
      // the visible cards instead of stacking zero-height ghosts.
      if (cardEl.classList.contains('is-pref-hidden')) return null
      return { id: c.id, cardEl, anchorEl, noConnector: !markEl, color: commentAccent(c) }
    })
    .filter((x): x is LayoutItem => x !== null)
  layoutMargin({
    marginArea,
    connectorsSvg: svg,
    comments: items,
    activeId: tab.activeCommentId || null,
  })
}

// ---------- active / hover linking ----------
function setActiveComment(commentId: string | null, focusTextarea: boolean): void {
  const prose = proseEl.value
  const marginArea = marginEl.value
  if (!prose || !marginArea) return
  tab.activeCommentId = commentId
  marginArea.querySelectorAll('.margin-card').forEach((c) => {
    c.classList.toggle('is-active', (c as HTMLElement).dataset.commentId === commentId)
  })
  prose.querySelectorAll('mark[data-comment-id]').forEach((m) => {
    m.classList.toggle('is-active', (m as HTMLElement).dataset.commentId === commentId)
  })
  relayout()
  if (focusTextarea && commentId) {
    const card = marginArea.querySelector(`.margin-card[data-comment-id="${commentId}"]`)
    card?.querySelector<HTMLTextAreaElement>('.card-note')?.focus()
  }
}

function setHoverComment(commentId: string, on: boolean): void {
  const prose = proseEl.value
  const marginArea = marginEl.value
  if (!prose || !marginArea) return
  prose
    .querySelectorAll(`mark[data-comment-id="${commentId}"]`)
    .forEach((el) => el.classList.toggle('is-hover', on))
  marginArea
    .querySelectorAll(`.margin-card[data-comment-id="${commentId}"]`)
    .forEach((el) => el.classList.toggle('is-hover', on))
}

function scrollHighlightIntoView(commentId: string): void {
  const m = proseEl.value?.querySelector(`mark[data-comment-id="${commentId}"]`)
  m?.scrollIntoView({ block: 'center', behavior: scrollBehavior() })
}

/** Scroll the document so a comment's highlight sits near the top of the reading
 *  area (used on mobile focus, where the sheet occupies the bottom). */
function scrollHighlightToTop(commentId: string): void {
  const mark = proseEl.value?.querySelector(`mark[data-comment-id="${commentId}"]`)
  const scroll = scrollEl.value
  if (!mark || !scroll) return
  const markRect = mark.getBoundingClientRect()
  const scrollRect = scroll.getBoundingClientRect()
  const top = scroll.scrollTop + (markRect.top - scrollRect.top) - 16
  scroll.scrollTo({ top: Math.max(0, top), behavior: scrollBehavior() })
}

const markHandlers = {
  onClick: (commentId: string) => focusComment(commentId),
  onEnter: (commentId: string) => setHoverComment(commentId, true),
  onLeave: (commentId: string) => setHoverComment(commentId, false),
}

/** Wrap a comment's highlight. Stored DOM offsets are tried first — but only
 *  trusted when the range they produce actually reproduces `quotedText`, so
 *  offsets from another revision (or seeded records that omit them) can never
 *  highlight the wrong words. Anything the offsets can't place re-anchors by
 *  searching the live prose for the quote. Returns true if a highlight landed. */
function applyHighlight(comment: Comment): boolean {
  const prose = proseEl.value
  if (!prose) return false
  const collapse = (s: string) => s.replace(/\s+/g, ' ').trim()
  let range: Range | null = null
  if (comment.domRangeEnd > comment.domRangeStart) {
    const byOffsets = makeRangeFromOffsets(prose, comment.domRangeStart, comment.domRangeEnd)
    if (
      byOffsets &&
      (!comment.quotedText || collapse(byOffsets.toString()) === collapse(comment.quotedText))
    ) {
      range = byOffsets
    }
  }
  if (!range && comment.quotedText) range = findTextRange(prose, comment.quotedText)
  if (!range) return false
  // Agent-authored marks keep the accent styling keyed off data-origin.
  wrapRangeWithMark(range, comment.id, markHandlers, isAgentThread(comment) ? 'agent' : undefined)
  applyAuthorTint(comment)
  return true
}

/** Tint a comment's prose marks with its author's stable color so highlights
 *  group by writer the same way the cards do (current-review comments only).
 *  Called both after wrapping and on commit (the draft is wrapped before the
 *  author is stamped, so the mark needs re-tinting once it's a real comment). */
function applyAuthorTint(comment: Comment): void {
  const prose = proseEl.value
  if (!prose) return
  // Drafts have no stamped author yet — tint them as mine so the highlight is my
  // colour while I write, not a fallback yellow (re-tinted on commit anyway).
  // Agent marks keep their accent styling (data-origin), not an author tint.
  const authorId = comment.author?.id ?? (comment._draft ? myAuthor.value.id : null)
  if (!authorId || authorId === AGENT_AUTHOR_ID) return
  const color = authorColor(authorId)
  prose.querySelectorAll<HTMLElement>(`mark[data-comment-id="${comment.id}"]`).forEach((m) => {
    m.classList.add('has-author')
    m.style.setProperty('--author-color', color)
  })
}

/** Size a note textarea to fit its content (no scrollbar, grows as you type). */
function autoGrowNote(el: HTMLTextAreaElement): void {
  el.style.height = 'auto'
  // border-box: scrollHeight is content + padding; add the borders so the last
  // line isn't clipped. CSS min-height still provides the floor.
  el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`
}

// ---------- card rendering (imperative, mirrors prototype) ----------
function renderCard(comment: Comment): void {
  const marginArea = marginEl.value
  if (!marginArea) return
  const card = document.createElement('div')

  const mode = comment._draft ? 'draft' : comment._editing ? 'edit' : 'view'
  const agent = isAgentThread(comment)
  const resolved = isResolved(comment)
  // The opening note is editable/deletable only by its writer: an authored
  // comment matching my identity, or a legacy author-less record (pre-identity
  // comments were always written on this device). Anyone can reply/resolve.
  const mine = !agent && (!comment.author || comment.author.id === myAuthor.value.id)
  const replies = comment.replies ?? []
  // Resolved threads collapse to a single badge row — settled conversation
  // shouldn't take margin space until you ask. `_collapsed === false` means
  // the reviewer expanded it.
  const collapsible = resolved && mode === 'view'
  const collapsed = collapsible && comment._collapsed !== false
  // A resolved thread with no live highlight is orphaned: keep its quote
  // visible even when collapsed — it's the only remaining cue to its subject.
  const hasMark = !!proseEl.value?.querySelector(`mark[data-comment-id="${comment.id}"]`)
  card.className =
    'margin-card' +
    (mode === 'draft' ? ' is-draft' : '') +
    (mode === 'edit' ? ' is-editing' : '') +
    (agent ? ' origin-agent' : '') +
    (resolved ? ' is-resolved' : '') +
    (collapsible ? ' is-collapsible' : '') +
    (collapsed ? ' is-collapsed' : '') +
    (resolved && !hasMark ? ' is-orphaned' : '') +
    // Open thread whose passage is gone: stays open (never silently resolved),
    // parked at its heading with a "text changed" notice.
    (comment._anchorLost ? ' is-anchor-lost' : '')
  card.dataset.commentId = comment.id

  // Rail: tint with the opening author's stable color so cards group by
  // writer; agent threads keep the accent rail (origin-agent class above).
  // My own draft has no stamped author yet — colour it as mine so the rail
  // matches the highlight from the first keystroke (author stamped on commit).
  const railAuthorId = comment.author?.id ?? (comment._draft ? myAuthor.value.id : null)
  if (railAuthorId && railAuthorId !== AGENT_AUTHOR_ID) {
    card.classList.add('has-author')
    card.style.setProperty('--author-color', authorColor(railAuthorId))
  }
  // The card's accent (its highlight hue) drives the active-outline border so it
  // matches the connector + mark instead of a fixed yellow. See commentAccent.
  card.style.setProperty('--card-accent', commentAccent(comment))

  const truncated =
    comment.quotedText.length > 80 ? comment.quotedText.slice(0, 80) + '…' : comment.quotedText

  /** One message byline: color dot (or ✦ for the agent) + name + time. */
  const bylineFor = (author: { id: string; name: string }, at: number): string => {
    if (author.id === AGENT_AUTHOR_ID) {
      return `<div class="card-author is-agent"><span class="card-author-glyph" aria-hidden="true">✦</span><span class="card-author-name">${escapeHtml(AGENT_AUTHOR_NAME)}</span><span class="card-author-time"> · ${escapeHtml(relativeTime(at))}</span></div>`
    }
    return `<div class="card-author"><span class="card-author-dot" style="background:${authorColor(author.id)}" aria-hidden="true"></span><span class="card-author-name">${escapeHtml(author.name)}</span><span class="card-author-time"> · ${escapeHtml(relativeTime(at))}</span></div>`
  }

  let bodyHtml: string
  let metaHtml: string
  if (mode === 'draft' || mode === 'edit') {
    bodyHtml = `<textarea class="card-note" placeholder="Add a note…" spellcheck="false">${escapeHtml(comment.note || '')}</textarea>`
    metaHtml =
      mode === 'draft'
        ? `<button class="btn btn-ghost btn-sm card-cancel" type="button">Cancel</button>
           <button class="btn btn-primary btn-sm card-leave" type="button">Leave comment</button>`
        : `<button class="btn btn-ghost btn-sm card-cancel-edit" type="button">Cancel</button>
           <button class="btn btn-primary btn-sm card-save" type="button">Save</button>`
  } else if (mine) {
    const note = (comment.note || '').trim()
    bodyHtml = note
      ? `<div class="card-note-view" role="button" tabindex="0" title="Click to edit">${escapeHtml(note)}</div>`
      : `<div class="card-note-view card-note-empty" role="button" tabindex="0" title="Click to add a note">(add a note…)</div>`
    // When an author byline is shown it carries the timestamp, so the meta row
    // is just the delete action; legacy (author-less) cards keep the time here.
    const timeHtml = comment.author
      ? ''
      : `<span class="card-time">${escapeHtml(relativeTime(comment.createdAt))}</span>`
    metaHtml = `${timeHtml}
                <span class="card-actions">
                  <button class="btn btn-danger btn-icon card-delete" type="button" aria-label="Delete comment" title="Delete">×</button>
                </span>`
  } else {
    // Someone else's opening message (another reviewer or the agent): part of
    // the shared conversation — read-only body; respond via Reply below.
    bodyHtml = `<div class="card-note-readonly">${escapeHtml((comment.note || '').trim())}</div>`
    metaHtml = comment.author
      ? ''
      : `<span class="card-time">${escapeHtml(relativeTime(comment.createdAt))}</span>`
  }

  // Status badge: only resolved threads carry one (open is the unlabeled
  // default — one comment model, not four card types). Chevron collapses.
  const chevron = collapsible
    ? `<button class="card-collapse" type="button" aria-label="${collapsed ? 'Expand' : 'Collapse'}">${collapsed ? '▸' : '▾'}</button>`
    : ''
  const resolvedBy = comment.resolved
    ? comment.resolved.by === AGENT_AUTHOR_ID
      ? AGENT_AUTHOR_NAME
      : comment.author?.id === comment.resolved.by
        ? comment.author.name
        : (comment.replies ?? []).find((r) => r.author.id === comment.resolved?.by)?.author.name ??
          'Reviewer'
    : '' // badge renders only when resolved
  const badgeHtml = resolved
    ? `<div class="card-origin is-resolved"><span class="card-origin-glyph" aria-hidden="true">✓</span> <span class="card-origin-label">Resolved by ${escapeHtml(resolvedBy)}</span>${chevron}</div>`
    : ''

  const quoteHtml = `<div class="card-quote" title="${escapeAttr(comment.quotedText)}">${escapeHtml(truncated)}</div>`
  // Anchor-lost notice: the open thread's passage isn't in this revision, so
  // there's no highlight — say so instead of letting the missing connector
  // read as a rendering glitch (or worse, letting the thread pass as settled).
  const anchorLostHtml = comment._anchorLost
    ? `<div class="card-anchor-lost"><span aria-hidden="true">⚠</span> The text this pointed at changed in this revision</div>`
    : ''
  const openingByline = mode !== 'draft' && comment.author
    ? bylineFor(comment.author, comment.createdAt)
    : ''

  // The thread: replies render read-only under the opening message.
  const repliesHtml =
    mode === 'view'
      ? replies
          .map(
            (r) =>
              `<div class="card-reply">${bylineFor(r.author, r.createdAt)}<div class="card-note-readonly">${escapeHtml(r.note.trim())}</div></div>`,
          )
          .join('')
      : ''

  // Reply composer / thread actions (view mode only).
  let threadHtml = ''
  if (mode === 'view') {
    if (comment._replying) {
      threadHtml = `<div class="card-reply-compose">
        <textarea class="card-note card-reply-input" placeholder="Reply…" spellcheck="false">${escapeHtml(comment._replyDraft || '')}</textarea>
        <div class="card-meta">
          <button class="btn btn-ghost btn-sm card-reply-cancel" type="button">Cancel</button>
          <button class="btn btn-primary btn-sm card-reply-post" type="button">Reply</button>
        </div></div>`
    } else if (!collapsed) {
      const settleBtn = resolved
        ? `<button class="btn btn-ghost btn-sm card-reopen-btn" type="button" title="Re-open this thread">Re-open</button>`
        : `<button class="btn btn-ghost btn-sm card-resolve-btn" type="button" title="Mark this thread resolved">Resolve</button>`
      threadHtml = `<div class="card-thread-actions">
        <button class="btn btn-ghost btn-sm card-reply-btn" type="button">Reply</button>
        ${settleBtn}
      </div>`
    }
  }

  const metaRow = metaHtml.trim() ? `<div class="card-meta">${metaHtml}</div>` : ''
  card.innerHTML = `
    ${badgeHtml}
    ${quoteHtml}
    ${anchorLostHtml}
    ${openingByline}
    ${bodyHtml}
    ${repliesHtml}
    ${threadHtml}
    ${metaRow}
  `
  marginArea.appendChild(card)

  // Collapsible (resolved) threads: the chevron — and the badge row — toggle
  // the collapsed state in place.
  if (collapsible) {
    const toggle = (e: Event) => {
      e.stopPropagation()
      comment._collapsed = !collapsed
      replaceCard(comment)
    }
    card.querySelector<HTMLElement>('.card-collapse')?.addEventListener('click', toggle)
    card.querySelector<HTMLElement>('.card-origin')?.addEventListener('click', toggle)
  }

  // Thread actions: reply / resolve / re-open, available to every participant.
  card.querySelector<HTMLElement>('.card-reply-btn')?.addEventListener('click', (e) => {
    e.stopPropagation()
    startReply(comment)
  })
  card.querySelector<HTMLElement>('.card-resolve-btn')?.addEventListener('click', (e) => {
    e.stopPropagation()
    resolveThread(comment)
  })
  card.querySelector<HTMLElement>('.card-reopen-btn')?.addEventListener('click', (e) => {
    e.stopPropagation()
    reopenThread(comment)
  })
  const replyInput = card.querySelector<HTMLTextAreaElement>('.card-reply-input')
  if (replyInput) {
    replyInput.addEventListener('input', () => {
      comment._replyDraft = replyInput.value
      autoGrowNote(replyInput)
      relayout()
      refreshFocusedSheet()
    })
    replyInput.addEventListener('focus', () => setActiveComment(comment.id, false))
    replyInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        cancelReply(comment)
      } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        e.stopPropagation()
        postReply(comment)
      }
    })
    autoGrowNote(replyInput)
    card.querySelector<HTMLElement>('.card-reply-cancel')?.addEventListener('click', (e) => {
      e.stopPropagation()
      cancelReply(comment)
    })
    card.querySelector<HTMLElement>('.card-reply-post')?.addEventListener('click', (e) => {
      e.stopPropagation()
      postReply(comment)
    })
  }

  if (mode === 'draft' || mode === 'edit') {
    const noteEl = card.querySelector<HTMLTextAreaElement>('.card-note')!
    noteEl.addEventListener('input', () => {
      comment.note = noteEl.value
      autoGrowNote(noteEl)
      relayout() // the card just changed height — reflow the column
      refreshFocusedSheet() // and re-fit the focused sheet to the taller note
      if (mode === 'draft') return
      tab.updatedAt = Date.now()
      saveSoon()
    })
    noteEl.addEventListener('focus', () => setActiveComment(comment.id, false))
    autoGrowNote(noteEl) // fit any existing note on open (edit mode)
  }

  if (mode === 'draft') {
    const cancelBtn = card.querySelector<HTMLElement>('.card-cancel')!
    const leaveBtn = card.querySelector<HTMLElement>('.card-leave')!
    cancelBtn.addEventListener('mousedown', (e) => e.stopPropagation())
    leaveBtn.addEventListener('mousedown', (e) => e.stopPropagation())
    cancelBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      cancelDraft(comment)
    })
    leaveBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      commitDraft(comment)
    })
  } else if (mode === 'edit') {
    const cancelEditBtn = card.querySelector<HTMLElement>('.card-cancel-edit')!
    const saveBtn = card.querySelector<HTMLElement>('.card-save')!
    cancelEditBtn.addEventListener('mousedown', (e) => e.stopPropagation())
    saveBtn.addEventListener('mousedown', (e) => e.stopPropagation())
    cancelEditBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      cancelEdit(comment)
    })
    saveBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      saveEdit(comment)
    })
  } else if (mine) {
    const noteView = card.querySelector<HTMLElement>('.card-note-view')!
    const openEdit = (e: Event) => {
      e.stopPropagation()
      startEdit(comment)
    }
    // Desktop: tapping the note text edits it. Touch: that's too context-switchy
    // when you just want to *select* the comment, so a tap on the note focuses
    // the comment instead (editing is the explicit pencil). An empty note still
    // opens the editor on tap — there's nothing to select, you want to write.
    noteView.addEventListener('click', (e) => {
      e.stopPropagation()
      if (isMobile() && !noteView.classList.contains('card-note-empty')) {
        focusComment(comment.id)
      } else {
        startEdit(comment)
      }
    })
    noteView.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') openEdit(e)
    })
    card.querySelector<HTMLElement>('.card-delete')?.addEventListener('click', (e) => {
      e.stopPropagation()
      deleteComment(comment.id)
    })
  }

  // The whole card is a generous "select this comment" landing pad — anywhere
  // outside the explicit action buttons / inputs focuses the comment.
  card.addEventListener('click', (e) => {
    const t = e.target as HTMLElement
    if (
      t.closest(
        '.card-delete, .card-cancel, .card-leave, .card-cancel-edit, .card-save, .card-edit, .card-note-view, .card-reply-btn, .card-resolve-btn, .card-reopen-btn, .card-reply-cancel, .card-reply-post',
      )
    )
      return
    if (t.closest('.card-note')) return
    focusComment(comment.id)
  })
  card.addEventListener('mouseenter', () => setHoverComment(comment.id, true))
  card.addEventListener('mouseleave', () => setHoverComment(comment.id, false))
}

function replaceCard(comment: Comment): void {
  marginEl.value?.querySelector(`.margin-card[data-comment-id="${comment.id}"]`)?.remove()
  renderCard(comment)
  relayout()
}

// ---------- selection → draft ----------
function maybeStartDraftFromSelection(): void {
  const sel = window.getSelection()
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return
  startDraftFromRange(sel.getRangeAt(0))
}

/** Start a draft from an explicit Range. createDraft reads the Range directly
 *  (offsets, text, enclosing block) and never touches window.getSelection() —
 *  so this works even when the live selection has been collapsed, which is the
 *  norm on touch once the user taps the "+ Comment" pill. */
function startDraftFromRange(range: Range): void {
  if (props.plain) return // reading-only docs don't take comments
  if (draftCommentId) return
  if (editingCommentId) return
  if (!props.active) return
  const prose = proseEl.value
  if (!prose) return
  // Clamp instead of reject: a selection that overshoots the last line (into
  // the prose padding / margin column) keeps its endpoint outside .prose, and
  // the old commonAncestor check would silently drop it.
  const clamped = clampRangeToProse(range, prose)
  if (!clamped) return
  if (!clamped.toString().trim()) return
  createDraft(clamped)
}

function createDraft(range: Range): void {
  const prose = proseEl.value
  if (!prose) return
  const quotedText = range.toString().replace(/\s+/g, ' ').trim()

  const startBlock = findEnclosingBlock(range.startContainer, prose)
  const endBlock = findEnclosingBlock(range.endContainer, prose)
  let sourceLineStart = 0
  let sourceLineEnd = 0
  let blockHint = ''
  if (startBlock && endBlock) {
    sourceLineStart = approxLineWithinBlock(startBlock, range.startContainer, range.startOffset, 'start')
    sourceLineEnd = approxLineWithinBlock(endBlock, range.endContainer, range.endOffset, 'end')
    if (sourceLineEnd < sourceLineStart) sourceLineEnd = sourceLineStart
    blockHint = findPrecedingHeading(startBlock)
  }

  const startInProse = charOffsetWithin(prose, range.startContainer, range.startOffset)
  const endInProse = charOffsetWithin(prose, range.endContainer, range.endOffset)

  const comment: Comment = {
    id: crypto.randomUUID(),
    sourceLineStart,
    sourceLineEnd,
    quotedText,
    blockHint,
    domRangeStart: startInProse,
    domRangeEnd: endInProse,
    note: '',
    createdAt: Date.now(),
    _draft: true,
  }
  tab.comments.push(comment)
  syncSheetCounts() // a draft now exists → hasCards flips true so the sheet shows
  applyHighlight(comment)
  renderCard(comment)
  const mobile = isMobile()
  if (mobile) {
    // Reveal + expand the sheet *synchronously*. The reactive :class lags a
    // tick, but the draft card lives inside the sheet — if it's still
    // display:none when we focus, iOS can't move focus there and no keyboard
    // appears. Toggle the classes by hand now (Vue's next patch agrees: hasCards
    // is true and the mode is expanded, so it won't strip them), then focus the
    // note inside the tap gesture so the keyboard rises.
    sheetMode.value = 'expanded'
    focusedCommentId.value = null
    marginEl.value?.classList.add('has-comments', 'is-expanded')
    // The native selection (and its OS callout) has done its job; clear it so
    // the comment textarea — not the page selection — owns focus.
    window.getSelection()?.removeAllRanges()
    setActiveComment(comment.id, true)
    nextTick(() => {
      marginEl.value
        ?.querySelector(`.margin-card[data-comment-id="${comment.id}"]`)
        ?.scrollIntoView({ block: 'nearest', behavior: scrollBehavior() })
    })
  } else {
    setActiveComment(comment.id, true)
    // Wrapping the dragged text in <mark> elements invalidates the browser's
    // original Range. Re-select the new marks so Cmd/Ctrl+C still copies the
    // quote. This deliberately wins over keeping the textarea focused: copying
    // the passage immediately after highlighting is a core reader interaction.
    preserveCommentSelection(prose, comment.id)
  }
  startDraftMode(comment.id)
}

function startDraftMode(commentId: string): void {
  draftCommentId = commentId
  document.addEventListener('mousedown', onDraftOutsideMousedown, true)
  document.addEventListener('keydown', onDraftKeydown, true)
}
function endDraftMode(): void {
  draftCommentId = null
  document.removeEventListener('mousedown', onDraftOutsideMousedown, true)
  document.removeEventListener('keydown', onDraftKeydown, true)
}

function onDraftOutsideMousedown(e: MouseEvent): void {
  if (!draftCommentId) return
  const card = marginEl.value?.querySelector(`.margin-card[data-comment-id="${draftCommentId}"]`)
  if (card && card.contains(e.target as Node)) return
  const comment = tab.comments.find((c) => c.id === draftCommentId)
  if (!comment) {
    endDraftMode()
    return
  }
  if ((comment.note || '').trim()) commitDraft(comment)
  else cancelDraft(comment)
  // The draft's text is still natively selected (preserveCommentSelection, for
  // Cmd+C). Drop it: otherwise this same click's mouseup — e.g. a click in the
  // empty space beside a heading, which doesn't collapse a selection — finds a
  // live selection and starts a new draft from it, re-highlighting a resized
  // range (its endpoints were pinned around the marks we just unwrapped).
  window.getSelection()?.removeAllRanges()
}

function onDraftKeydown(e: KeyboardEvent): void {
  if (!draftCommentId) return
  const comment = tab.comments.find((c) => c.id === draftCommentId)
  if (!comment) {
    endDraftMode()
    return
  }
  if (e.key === 'Escape') {
    e.preventDefault()
    e.stopPropagation()
    cancelDraft(comment)
  } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
    e.preventDefault()
    e.stopPropagation()
    commitDraft(comment)
  }
}

function commitDraft(comment: Comment): void {
  delete comment._draft
  comment.note = (comment.note || '').trim()
  if (!comment.author) comment.author = { ...myAuthor.value } // stamp the writer
  applyAuthorTint(comment) // re-tint: the mark was wrapped before the author existed
  marginEl.value?.querySelector(`.margin-card[data-comment-id="${comment.id}"]`)?.remove()
  renderCard(comment)
  endDraftMode()
  tab.updatedAt = Date.now()
  relayout()
  saveNow()
  recordMetric('comment_created')
}

function cancelDraft(comment: Comment): void {
  tab.comments = tab.comments.filter((c) => c.id !== comment.id)
  syncSheetCounts() // draft gone → if it was the only card, the sheet hides again
  if (tab.activeCommentId === comment.id) tab.activeCommentId = null
  const prose = proseEl.value
  if (prose) unwrapMarks(prose, comment.id)
  marginEl.value?.querySelector(`.margin-card[data-comment-id="${comment.id}"]`)?.remove()
  relayout()
  endDraftMode()
}

// ---------- edit transitions ----------
function startEdit(comment: Comment): void {
  if (editingCommentId && editingCommentId !== comment.id) {
    const other = tab.comments.find((c) => c.id === editingCommentId)
    if (other) saveEdit(other)
  }
  comment._editing = true
  comment._editPrev = comment.note
  editingCommentId = comment.id
  document.addEventListener('mousedown', onEditOutsideMousedown, true)
  document.addEventListener('keydown', onEditKeydown, true)
  replaceCard(comment)
  const note = marginEl.value
    ?.querySelector(`.margin-card[data-comment-id="${comment.id}"]`)
    ?.querySelector<HTMLTextAreaElement>('.card-note')
  if (note) {
    note.focus()
    note.setSelectionRange(note.value.length, note.value.length)
  }
  setActiveComment(comment.id, false)
  refreshFocusedSheet() // the card just grew into an editor — re-fit the sheet
}

function saveEdit(comment: Comment): void {
  comment.note = (comment.note || '').trim()
  delete comment._editing
  delete comment._editPrev
  endEditMode()
  tab.updatedAt = Date.now()
  replaceCard(comment)
  refreshFocusedSheet()
  saveNow()
}

function cancelEdit(comment: Comment): void {
  comment.note = comment._editPrev ?? ''
  delete comment._editing
  delete comment._editPrev
  endEditMode()
  tab.updatedAt = Date.now()
  replaceCard(comment)
  refreshFocusedSheet()
  saveNow()
}

function endEditMode(): void {
  editingCommentId = null
  document.removeEventListener('mousedown', onEditOutsideMousedown, true)
  document.removeEventListener('keydown', onEditKeydown, true)
}

function onEditOutsideMousedown(e: MouseEvent): void {
  if (!editingCommentId) return
  const card = marginEl.value?.querySelector(`.margin-card[data-comment-id="${editingCommentId}"]`)
  if (card && card.contains(e.target as Node)) return
  const comment = tab.comments.find((c) => c.id === editingCommentId)
  if (!comment) {
    endEditMode()
    return
  }
  saveEdit(comment)
}

function onEditKeydown(e: KeyboardEvent): void {
  if (!editingCommentId) return
  const comment = tab.comments.find((c) => c.id === editingCommentId)
  if (!comment) {
    endEditMode()
    return
  }
  if (e.key === 'Escape') {
    e.preventDefault()
    e.stopPropagation()
    cancelEdit(comment)
  } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
    e.preventDefault()
    e.stopPropagation()
    saveEdit(comment)
  }
}

// ---------- thread actions (reply / resolve / re-open) ----------
function startReply(comment: Comment): void {
  comment._replying = true
  replaceCard(comment)
  setActiveComment(comment.id, false)
  marginEl.value
    ?.querySelector(`.margin-card[data-comment-id="${comment.id}"]`)
    ?.querySelector<HTMLTextAreaElement>('.card-reply-input')
    ?.focus()
  refreshFocusedSheet()
}

function cancelReply(comment: Comment): void {
  delete comment._replying
  delete comment._replyDraft
  replaceCard(comment)
  refreshFocusedSheet()
}

function postReply(comment: Comment): void {
  const text = (comment._replyDraft || '').trim()
  if (!text) {
    cancelReply(comment)
    return
  }
  const now = Date.now()
  // A reply is an append-only message in the thread.
  comment.replies = [
    ...(comment.replies ?? []),
    { id: crypto.randomUUID(), author: { ...myAuthor.value }, note: text, createdAt: now },
  ]
  delete comment._replying
  delete comment._replyDraft
  tab.updatedAt = now
  replaceCard(comment)
  refreshFocusedSheet()
  saveNow()
}

function resolveThread(comment: Comment): void {
  const now = Date.now()
  comment.resolved = { at: now, by: myAuthor.value.id }
  delete comment._collapsed // resolved default: collapsed badge row
  delete comment._replying
  delete comment._replyDraft
  comment._anchorLost = false
  tab.updatedAt = now
  replaceCard(comment)
  applyReviewPrefs() // honors a hide-resolved filter immediately (ends in relayout)
  saveNow()
}

function reopenThread(comment: Comment): void {
  const now = Date.now()
  delete comment.resolved
  comment._collapsed = false
  tab.updatedAt = now
  // Re-render from state: the thread may need its anchor-lost notice recomputed
  // now that it's open again (rerender recomputes both transients).
  rerenderFromState()
  saveNow()
}

function deleteComment(commentId: string): void {
  // Comments are local state, so deletion is an ordinary array removal. Keep
  // the removed value in the toast closure for a short, recoverable Undo.
  const comment = tab.comments.find((c) => c.id === commentId)
  if (!comment) return
  const index = tab.comments.indexOf(comment)
  const undoable = !comment._draft
  tab.comments.splice(index, 1)
  if (tab.activeCommentId === commentId) tab.activeCommentId = null
  const prose = proseEl.value
  if (prose) unwrapMarks(prose, commentId)
  marginEl.value?.querySelector(`.margin-card[data-comment-id="${commentId}"]`)?.remove()
  tab.updatedAt = Date.now()
  relayout()
  saveNow()
  if (undoable) {
    pushToast('Comment deleted', 'info', {
      label: 'Undo',
      onAction: () => restoreDeletedComment(comment, index),
    })
  }
}

function restoreDeletedComment(comment: Comment, index: number): void {
  if (tab.comments.some((c) => c.id === comment.id)) return
  tab.comments.splice(Math.min(index, tab.comments.length), 0, comment)
  tab.updatedAt = Date.now()
  rerenderFromState()
  saveNow()
}

// ---------- mount / render ----------
function renderProse(): void {
  const prose = proseEl.value
  if (!prose) return
  prose.innerHTML = renderMarkdownWithLineInfo(tab.markdown)
  highlightCodeBlocks(prose)
  // Rehydrate persisted comments.
  for (const c of tab.comments) {
    const anchored = applyHighlight(c)
    // An OPEN thread that can't anchor stays visibly open — an explicit status
    // always beats an inference from a failed text search (the passage most
    // likely to have changed is exactly the one the comment criticized). The
    // card parks at its heading with a "text changed" notice.
    c._anchorLost = !anchored && !c._draft && !isResolved(c)
    renderCard(c)
  }
}

/**
 * Re-render prose + cards from the current `tab.comments`, discarding any
 * existing marks/cards first. Used when comments are seeded into an
 * already-mounted tab (for example, after loading a carried thread snapshot).
 */
function rerenderFromState(): void {
  marginEl.value?.querySelectorAll('.margin-card').forEach((c) => c.remove())
  renderProse()
  refreshDocMeta()
  requestAnimationFrame(() => applyReviewPrefs())
}

onMounted(() => {
  renderProse()

  const scroll = scrollEl.value!
  scroll.addEventListener('scroll', () => {
    if (scrollTick) return
    scrollTick = setTimeout(() => {
      scrollTick = null
      tab.scrollTop = scroll.scrollTop
      if (!props.ephemeral) saveTabSoon(tab)
    }, 500)
  })

  ro = new ResizeObserver(() => {
    if (!props.active) return
    relayout()
  })
  ro.observe(rootEl.value!.querySelector('.prose-grid')!)

  document.addEventListener('selectionchange', onSelectionChange)

  refreshDocMeta()
  if (props.active) {
    scroll.scrollTop = tab.scrollTop || 0
    requestAnimationFrame(() => applyReviewPrefs())
  }
})

// React to becoming active.
watch(
  () => props.active,
  (now) => {
    if (!now) return
    nextTick(() => {
      const scroll = scrollEl.value
      if (scroll) scroll.scrollTop = tab.scrollTop || 0
      refreshDocMeta()
      requestAnimationFrame(() => applyReviewPrefs())
    })
  },
)

onBeforeUnmount(() => {
  ro?.disconnect()
  if (pillTimer) clearTimeout(pillTimer)
  document.removeEventListener('selectionchange', onSelectionChange)
  endDraftMode()
  endEditMode()
})

/** The document's heading structure, for the outline panel. Read straight off
 *  the rendered prose so it always matches what's on screen. */
function getOutline(): { level: number; text: string }[] {
  const prose = proseEl.value
  if (!prose) return []
  return Array.from(prose.querySelectorAll('h1, h2, h3, h4, h5, h6'))
    .map((h) => ({ level: Number(h.tagName[1]), text: (h.textContent || '').trim() }))
    .filter((h) => h.text.length > 0)
}
/** Scroll the Nth heading (same order/filtering as getOutline) into view. */
function scrollToHeading(index: number): void {
  const prose = proseEl.value
  if (!prose) return
  const headings = Array.from(prose.querySelectorAll('h1, h2, h3, h4, h5, h6')).filter(
    (h) => (h.textContent || '').trim().length > 0,
  )
  ;(headings[index] as HTMLElement | undefined)?.scrollIntoView({
    block: 'start',
    behavior: scrollBehavior(),
  })
}

defineExpose({
  maybeStartDraftFromSelection,
  refreshDocMeta,
  rerenderFromState,
  getOutline,
  scrollToHeading,
  hash: tab.hash,
})
</script>

<template>
  <section ref="rootEl" class="doc-view" :class="{ 'is-plain': plain }" :hidden="!active">
    <div ref="scrollEl" class="doc-scroll">
      <div class="prose-grid">
        <div class="doc-meta-wrap"><div ref="metaEl" class="doc-meta"></div></div>
        <article ref="proseEl" class="prose"></article>
        <!-- Mobile-only: tap the dimmed doc behind the full list to dismiss it.
             Only in expanded mode — focused mode leaves the doc clear so its
             highlight reads. Light tint for context; hidden on desktop. -->
        <div v-if="!plain && sheetMode === 'expanded'" class="sheet-scrim" @click="collapseSheet"></div>
        <div
          ref="marginEl"
          class="margin-area"
          :class="{
            'has-comments': hasCards,
            'is-expanded': sheetMode === 'expanded',
            'is-focused': sheetMode === 'focused',
          }"
        >
          <!-- Mobile-only: collapses the gutter into a draggable bottom sheet.
               Hidden on desktop via CSS; the cards inside render the same. A
               div (not button) so the pager buttons can nest inside it. -->
          <div
            v-if="!plain"
            class="sheet-handle"
            role="button"
            tabindex="0"
            :aria-expanded="sheetMode !== 'collapsed'"
            @pointerdown="onHandlePointerDown"
            @pointermove="onHandlePointerMove"
            @pointerup="onHandlePointerUp"
            @pointercancel="onHandlePointerUp"
            @click="onHandleClick"
            @keydown.enter.prevent="toggleSheet"
            @keydown.space.prevent="toggleSheet"
          >
            <span class="sheet-grip" aria-hidden="true"></span>
            <!-- Prev / count / Next. The buttons step through comments in reading
                 order and disable at the ends. stop the drag/toggle so a tap
                 pages instead of moving the sheet. -->
            <div class="sheet-bar">
              <button
                v-if="commentCount > 1"
                type="button"
                class="sheet-nav-btn"
                :disabled="prevDisabled"
                @pointerdown.stop
                @click.stop="focusAdjacent(-1)"
              >Prev</button>
              <span class="sheet-label">
                {{ commentCount }} {{ commentCount === 1 ? 'comment' : 'comments' }}
              </span>
              <button
                v-if="commentCount > 1"
                type="button"
                class="sheet-nav-btn"
                :disabled="nextDisabled"
                @pointerdown.stop
                @click.stop="focusAdjacent(1)"
              >Next</button>
            </div>
          </div>
          <svg ref="connectorsEl" class="connectors" xmlns="http://www.w3.org/2000/svg"></svg>
        </div>
      </div>
    </div>
    <!-- Quick review filter (EXPERIMENTAL): mute the agent / resolved layers
         from the reader. Mirrors the Settings toggles (same persisted prefs);
         shown only when the doc carries those layers. -->
    <div v-if="!plain && (agentCount > 0 || resolvedCount > 0)" class="review-filter">
      <button
        v-if="agentCount > 0"
        type="button"
        class="review-filter-chip"
        :class="{ 'is-off': !showAgent }"
        :title="showAgent ? 'Hide agent notes' : 'Show agent notes'"
        :aria-pressed="showAgent"
        @click="toggleAgent"
      >
        <span class="rf-glyph rf-agent" aria-hidden="true">✦</span> Agent
      </button>
      <button
        v-if="resolvedCount > 0"
        type="button"
        class="review-filter-chip"
        :class="{ 'is-off': !showResolved }"
        :title="showResolved ? 'Hide resolved' : 'Show resolved'"
        :aria-pressed="showResolved"
        @click="toggleResolved"
      >
        <span class="rf-glyph rf-resolved" aria-hidden="true">✓</span> Resolved
      </button>
    </div>
    <!-- Mobile: floats over a live text selection so touch users have an
         explicit "start a comment" affordance (no mouseup to hook). -->
    <button v-if="pillVisible" type="button" class="comment-pill" @click="onCommentPill">
      + Comment
    </button>
  </section>
</template>
